/**
 * An invoice, on the shared record page.
 *
 * Until now an invoice had no page at all: the Invoices screen's rows and its
 * "Needs you today" cards both navigated to `/invoices/:id`, which matched
 * nothing and fell through to Home. So the one document a customer argues
 * about was the one record nobody could open, and there was nowhere to put
 * the photo of the signed delivery note or the note that says who rang.
 *
 * `docs/record-page-contract.md` is the rulebook. The two tabs that are this
 * record's own are the two questions an invoice is opened to answer: what is
 * on it, and what has come off it.
 *
 * **No related-list tab.** A consolidated invoice covers many jobs, and
 * rolling their visits, files and system events onto it is exactly the noise
 * `expandLinks` refuses to create in the other direction. The jobs it bills
 * are in the rail, one click from each.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, type NavigateFunction } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { invoiceMark } from '../components/landing-marks.js'
import { RecordPage, type RecordConfig } from '../record/record-page.js'
import { renderStandardTab, standardTabs } from '../record/record-standard.js'
import { AboutCard, RecordCard, RelatedRow, type AboutField } from '../record/record-rail.js'
import { completeActivity, useRecordSummary } from '../lib/use-record.js'
import { SettleBalance } from '../components/settle-balance.js'
import {
  Button, Card, EmptyState, SectionLabel, StatusChip, Tag, humanize,
} from '../components/primitives.js'
import { Toast } from '../components/overlays.js'
import type { MenuItem } from '../components/context-menu.js'
import {
  NAV_ICONS, IconCompany, IconEmail, IconNext, IconNote, IconUpload,
} from '../frame/icons.js'
import {
  EM_DASH, formatDocumentDate, formatDollars, formatListDate, formatRelative,
} from '../lib/format.js'
import { request } from '../lib/api.js'

interface InvoiceLine {
  id: string
  kind: string
  description: string
  qty: string
  rate: string
  amount: string
  taxAmount: string
  jobId: string | null
  propertyId: string | null
  jobReference: string | null
  propertyName: string | null
}

/** A payment, a credit or a write-off, as one list — §F.2 keeps the three apart. */
interface Settlement {
  id: string
  kind: 'payment' | 'credit' | 'write_off'
  amount: string
  detail: string | null
  status: string | null
  at: string
}

interface InvoicePayload {
  id: string
  reference: string
  kind: string
  status: string
  statusReason: string | null
  issueDate: string | null
  dueDate: string | null
  termsDays: number | null
  subtotal: string
  taxTotal: string
  total: string
  balance: string
  sentAt: string | null
  viewedAt: string | null
  inDispute: boolean
  bounced: boolean
  paymentPending: boolean
  poNumber: string | null
  periodStart: string | null
  periodEnd: string | null
  reminderStep: number | null
  nextReminderOn: string | null
  accountId: string | null
  billToContactId: string | null
  customer: string | null
  billTo: string | null
  billToEmail: string | null
  lines: InvoiceLine[]
  reminders: {
    step: number
    dueOn: string
    channel: string
    sentAt: string | null
    heldReason: string | null
  }[]
  settlements: Settlement[]
}

/** The shape the config closes over — the payload, plus what only this page knows. */
interface InvoiceRecordData extends InvoicePayload {
  canEdit: boolean
  canSettle: boolean
  orgUnitId: string
  onResend: () => void
  onSettle: () => void
  onReload: () => void
  onUpload: () => void
}

/** The jobs an invoice bills, in the order its lines name them. */
function jobsOf(invoice: InvoicePayload): { id: string; label: string; amount: number }[] {
  const seen = new Map<string, { id: string; label: string; amount: number }>()
  for (const line of invoice.lines) {
    if (!line.jobId) continue
    const found = seen.get(line.jobId)
    if (found) {
      found.amount += Number(line.amount)
    } else {
      seen.set(line.jobId, {
        id: line.jobId,
        label: line.propertyName ?? line.jobReference ?? 'A job',
        amount: Number(line.amount),
      })
    }
  }
  return [...seen.values()]
}

export function InvoiceRecord({ me }: { me: MeResponse }) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<InvoicePayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [people, setPeople] = useState<{ id: string; name: string }[]>([])
  const [settling, setSettling] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [completing, setCompleting] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const openUpload = useRef<(() => void) | null>(null)

  const { summary, reload: reloadSummary } = useRecordSummary('invoice', id || undefined)

  const canEdit = me.permissions.invoices === 'edit' || me.permissions.invoices === 'full'
  const canSettle = me.permissions.invoices === 'full'
  const orgUnitId = me.scope.selected[0] ?? ''

  const load = useCallback(async () => {
    try {
      setData(await request<InvoicePayload>(`/invoices/${id}`))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That invoice could not be loaded.')
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    void request<{ items: { id: string; name: string }[] }>('/people?limit=200')
      .then((body) => setPeople(body.items))
      .catch(() => setPeople([]))
  }, [])

  const complete = useCallback(async (activityId: string) => {
    setCompleting(activityId)
    try {
      /* One occurrence of a repeating meeting by itself (`completeKey`, 0152). */
      await completeActivity(activityId)
      await reloadSummary()
    } finally {
      setCompleting(null)
    }
  }, [reloadSummary])

  const resend = useCallback(async () => {
    setBusy(true)
    try {
      const result = await request<{ delivered: boolean }>(
        `/invoices/${id}/resend`, { method: 'POST', body: {} })
      await load()
      await reloadSummary()
      setNotice(result.delivered
        ? 'Sent.'
        : 'Still nowhere to send it — the contact has no channel enabled for invoices.')
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }, [id, load, reloadSummary])

  const record = useMemo<InvoiceRecordData | null>(() => (data ? {
    ...data,
    canEdit,
    canSettle,
    orgUnitId,
    onResend: () => void resend(),
    onSettle: () => setSettling(true),
    onReload: () => { void load(); void reloadSummary() },
    onUpload: () => openUpload.current?.(),
  } : null), [data, canEdit, canSettle, orgUnitId, resend, load, reloadSummary])

  const config = useMemo(() => invoiceConfig(navigate), [navigate])

  if (error) {
    return (
      <EmptyState
        title="Not found"
        body={error}
        action={{ label: 'Back to invoices', onClick: () => navigate('/invoices') }}
      />
    )
  }
  if (!record) return <p className="p-4 text-cell text-text-muted">Loading…</p>

  return (
    <>
      <RecordPage config={config} record={record} summary={summary}>
        {(tab) => renderStandardTab(tab, {
          object: 'invoice',
          id: record.id,
          href: `/invoices/${record.id}`,
          summary,
          people,
          orgUnitId,
          /*
           * The invoice, and the lines under it. A line's amount changing is
           * the change somebody disputes, and it is not a column on
           * `invoices` — so History is told about them, the way a job's is
           * told about its status moves.
           */
          history: {
            table: 'invoices',
            related: {
              tables: ['invoice_lines'],
              recordIds: record.lines.map((line) => line.id),
            },
          },
          busyId: completing,
          canEdit,
          onComplete: (activityId) => void complete(activityId),
          onChanged: () => void reloadSummary(),
          onUploadReady: (open) => { openUpload.current = open },
          onOpen: (path) => navigate(path),
        }) ?? (
          tab === 'lines'
            ? <Lines invoice={record} navigate={navigate} />
            : tab === 'money'
              ? <Money invoice={record} />
              : null
        )}
      </RecordPage>

      <SettleBalance
        open={settling}
        onClose={() => setSettling(false)}
        invoice={{
          id: record.id,
          reference: record.reference,
          balance: Number(record.balance),
        }}
        onDone={(message) => { setSettling(false); setNotice(message); record.onReload() }}
      />

      {(notice || busy) && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{busy ? 'Sending…' : notice}</Toast>
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// The configuration
// ---------------------------------------------------------------------------

function invoiceConfig(navigate: NavigateFunction): RecordConfig<InvoiceRecordData> {
  return {
    object: 'invoice',

    breadcrumb: (record) => [
      { label: 'Invoices', href: '/invoices' },
      { label: record.reference },
    ],

    /*
     * The reference in a square tinted by where the invoice has got to — the
     * same square the list draws, because the row somebody clicked and the
     * page it opened should be recognisably the same object.
     */
    /*
     * The ring that fills as the invoice is paid, at 56 (`list-marks.md` §3)
     * — the same mark the list row and the customer's pay page draw, so the
     * three surfaces agree about how much has come in.
     */
    mark: (record) => invoiceMark({
      id: record.id,
      status: record.status,
      amount: record.total,
      balance: record.balance,
    }, 56),

    /*
     * The customer, not the reference. The reference is in the breadcrumb and
     * on the mark; what somebody needs off the top of an invoice is whose it
     * is, which is the first thing they say out loud about it.
     */
    title: (record) => record.customer ?? record.reference,

    /*
     * The status, then the flags. §F.3: Overdue, Disputed and Bounced are
     * flags on top of a status rather than statuses, and two chips side by
     * side say that where one merged chip would not.
     */
    tags: (record) => {
      const overdue = record.dueDate !== null
        && record.dueDate < new Date().toISOString().slice(0, 10)
        && Number(record.balance) > 0
        && !['draft', 'paid', 'voided'].includes(record.status)
      return [
        {
          label: humanize(record.status),
          tone: {
            fg: `rgb(var(--status-invoice-${record.status}-fg))`,
            bg: `rgb(var(--status-invoice-${record.status}-bg))`,
          },
        },
        ...(overdue ? [{ label: 'Overdue', tone: flag('overdue') }] : []),
        ...(record.inDispute ? [{ label: 'Disputed', tone: flag('disputed') }] : []),
        ...(record.bounced ? [{ label: 'Bounced', tone: flag('bounced') }] : []),
      ].slice(0, 3)
    },

    meta: (record) => [
      ...(record.dueDate
        ? [{
          icon: <NAV_ICONS.invoices size={13} />,
          text: `Due ${formatListDate(record.dueDate)}${record.termsDays ? ` · net ${record.termsDays}` : ''}`,
        }]
        : []),
      ...(record.issueDate ? [{ text: `Issued ${formatListDate(record.issueDate)}` }] : []),
      /*
       * The bill-to, unless it is the customer said twice. On an account it
       * is a different person — "Old Orchard Business Park · Hector Mensah" —
       * and on an individual the two are the same name, which is a meta line
       * spending its width saying nothing.
       */
      ...(record.billTo && record.billTo !== record.customer
        ? [{
          icon: <IconCompany size={13} />,
          text: record.billTo,
          ...(record.billToContactId
            ? { onClick: () => navigate(`/contacts/${record.billToContactId!}`) }
            : {}),
        }]
        : []),
      ...(record.poNumber ? [{ text: `PO ${record.poNumber}` }] : []),
      ...(record.periodStart && record.periodEnd
        ? [{
          text: `${formatListDate(record.periodStart)} – ${formatListDate(record.periodEnd)}`,
        }]
        : []),
    ],

    /*
     * Sending it again is the one thing this page does that changes anything
     * in the world. It is offered only once the invoice has been sent at
     * least once: an invoice that has never gone out is approved from the
     * queue, which is where the batch of them is.
     */
    primary: (record) => (record.canEdit && record.sentAt
      ? { label: 'Send it again', onClick: record.onResend }
      : null),

    actions: (record) => (record.canSettle && Number(record.balance) > 0
      ? [{ label: 'Credit or write off', onClick: record.onSettle }]
      : []),

    menu: (record) => {
      const items: MenuItem[] = []
      if (record.accountId) {
        items.push({
          key: 'account',
          label: 'Open the customer',
          onSelect: () => navigate(`/contacts/${record.billToContactId ?? ''}`),
        })
      }
      return items
    },

    tabs: (record, summary) => standardTabs({
      counts: summary?.counts ?? {},
      own: [
        {
          key: 'lines',
          label: 'Lines',
          icon: 'invoices' as const,
          count: record.lines.length,
          // A consolidated invoice's lines are a table with a money column per
          // property; beside a 340px rail the descriptions lose their width.
          wide: true,
        },
        {
          key: 'money',
          label: 'Payments',
          icon: 'money' as const,
          count: record.settlements.length || undefined,
        },
      ],
    }),

    rail: (record) => <InvoiceRail record={record} navigate={navigate} />,

    phone: {
      /*
       * One: the address it was sent to. Somebody looking at an invoice on a
       * phone is usually about to ring or email about it, and the email is
       * the thing they cannot retype from memory.
       */
      quickActions: (record) => (record.billToEmail
        ? [{
          label: 'Email',
          icon: <IconEmail size={18} />,
          href: `mailto:${record.billToEmail}`,
        }]
        : []),
      tabBar: ['overview', 'lines', 'money', 'files'],
      primaryByTab: (tab, record) => {
        if (!record.canEdit || tab === 'activity') return null
        if (tab === 'files') {
          return { label: 'Upload', icon: <IconUpload size={18} />, onClick: record.onUpload }
        }
        if (tab === 'overview') {
          return {
            label: 'Log activity',
            icon: <IconNote size={18} />,
            onClick: () => navigate(`/invoices/${record.id}?tab=activity`),
          }
        }
        return record.sentAt
          ? { label: 'Send again', icon: <IconNext size={18} />, onClick: record.onResend }
          : null
      },
    },
  }
}

/** A flag's colours, from the invoice palette that already names all three. */
const flag = (key: 'overdue' | 'disputed' | 'bounced') => ({
  fg: `rgb(var(--status-invoice-${key}-fg))`,
  bg: `rgb(var(--status-invoice-${key}-bg))`,
})

// ---------------------------------------------------------------------------
// The rail
// ---------------------------------------------------------------------------

function InvoiceRail({
  record, navigate,
}: {
  record: InvoiceRecordData
  navigate: NavigateFunction
}) {
  const jobs = jobsOf(record)
  const waiting = record.reminders.filter((reminder) => !reminder.sentAt)

  const fields: AboutField[] = [
    { label: 'Kind', value: humanize(record.kind) },
    { label: 'Issued', value: record.issueDate ? formatDocumentDate(record.issueDate) : 'Not yet' },
    { label: 'Due', value: record.dueDate ? formatDocumentDate(record.dueDate) : EM_DASH },
    { label: 'Terms', value: record.termsDays ? `Net ${record.termsDays}` : EM_DASH },
    { label: 'PO number', value: record.poNumber || EM_DASH },
    {
      label: 'Sent',
      value: record.sentAt ? formatRelative(record.sentAt) : 'Not yet',
    },
    {
      label: 'Viewed',
      value: record.viewedAt ? formatRelative(record.viewedAt) : 'Not yet',
    },
    ...(record.statusReason ? [{ label: 'Why', value: record.statusReason }] : []),
  ]

  return (
    <>
      <RecordCard title="The money">
        <div className="px-[18px] pb-4 pt-3">
          <Figure label="Subtotal" value={Number(record.subtotal)} />
          <Figure label="Tax" value={Number(record.taxTotal)} />
          <Figure label="Total" value={Number(record.total)} strong />
          <div className="mt-2 border-t border-line-soft pt-2">
            <Figure label="Balance" value={Number(record.balance)} strong />
          </div>
          {record.paymentPending && (
            <p className="m-0 mt-2 rounded-field bg-warn-bg px-2 py-1.5 text-sub text-text">
              A payment is on its way — the balance does not count it until it settles.
            </p>
          )}
        </div>
      </RecordCard>

      <AboutCard fields={fields} />

      {record.billTo && (
        <RecordCard title="Bill to">
          <RelatedRow
            mark={(
              <span
                aria-hidden
                className="flex h-[26px] w-[26px] items-center justify-center rounded-field
                           bg-surface-alt text-text-muted"
              >
                <IconCompany size={14} />
              </span>
            )}
            title={record.billTo}
            sub={record.billToEmail ?? 'No email on file'}
            onOpen={() => record.billToContactId
              && navigate(`/contacts/${record.billToContactId}`)}
          />
        </RecordCard>
      )}

      {jobs.length > 0 && (
        <RecordCard
          title="What it bills"
          count={jobs.length}
          footer={jobs.length > 1
            ? 'A consolidated invoice — a payment is applied per line, so each job keeps its own received figure.'
            : undefined}
        >
          {jobs.map((job) => (
            <RelatedRow
              key={job.id}
              mark={(
                <span
                  aria-hidden
                  className="flex h-[26px] w-[26px] items-center justify-center rounded-field
                             bg-surface-alt text-text-muted"
                >
                  <NAV_ICONS.jobs size={14} />
                </span>
              )}
              title={job.label}
              trailing={(
                <span className="tnum text-body font-semibold text-text">
                  {formatDollars(job.amount)}
                </span>
              )}
              onOpen={() => navigate(`/jobs/${job.id}`)}
            />
          ))}
        </RecordCard>
      )}

      {record.reminders.length > 0 && (
        <RecordCard
          title="Reminders"
          count={record.reminders.length}
          footer={waiting.length === 0
            ? 'Every one of them has gone.'
            : `${waiting.length} still to send.`}
        >
          <div className="px-[18px] py-2">
            {record.reminders.map((reminder) => (
              <div
                key={reminder.step}
                className="flex items-baseline gap-2 border-b border-line-soft py-1.5 last:border-b-0"
              >
                <span className="min-w-0 flex-1 text-body text-text">
                  Step {reminder.step} · {humanize(reminder.channel)}
                </span>
                <span className="shrink-0 text-chip text-text-muted">
                  {reminder.sentAt
                    ? formatRelative(reminder.sentAt)
                    : reminder.heldReason
                      ? 'Held'
                      : formatListDate(reminder.dueOn)}
                </span>
              </div>
            ))}
          </div>
        </RecordCard>
      )}
    </>
  )
}

function Figure({ label, value, strong }: { label: string; value: number; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className={strong ? 'text-body font-semibold text-text' : 'text-body text-text-muted'}>
        {label}
      </span>
      <span className={strong
        ? 'tnum text-cell font-semibold text-text'
        : 'tnum text-cell text-text'}>
        {formatDollars(value)}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The two own tabs
// ---------------------------------------------------------------------------

/**
 * What is on it.
 *
 * Grouped by job where there is more than one, because that is what makes a
 * consolidated invoice readable — and because the group's own subtotal is the
 * figure a customer rings about ("what is the Ridge Street part of this?").
 */
function Lines({
  invoice, navigate,
}: {
  invoice: InvoiceRecordData
  navigate: NavigateFunction
}) {
  const jobs = jobsOf(invoice)
  const grouped = jobs.length > 1

  const groups = grouped
    ? jobs.map((job) => ({
      key: job.id,
      label: job.label,
      lines: invoice.lines.filter((line) => line.jobId === job.id),
    }))
    : [{ key: 'all', label: '', lines: invoice.lines }]

  const loose = grouped ? invoice.lines.filter((line) => !line.jobId) : []

  return (
    <Card className="overflow-hidden p-0">
      {invoice.lines.length === 0
        ? <p className="m-0 p-6 text-body text-text-muted">Nothing on it yet.</p>
        : (
          <>
            {[...groups, ...(loose.length > 0
              ? [{ key: 'loose', label: 'Not against a job', lines: loose }]
              : [])].map((group) => (
              <div key={group.key}>
                {group.label && (
                  <div className="flex items-baseline gap-2 border-b border-line-soft
                                  bg-surface-group px-3.5 py-2">
                    <SectionLabel>{group.label}</SectionLabel>
                    <span className="tnum ml-auto text-cell font-semibold text-text">
                      {formatDollars(group.lines.reduce(
                        (total, line) => total + Number(line.amount), 0))}
                    </span>
                    {group.lines[0]?.jobId && (
                      <Button
                        variant="ghost"
                        onClick={() => navigate(`/jobs/${group.lines[0]!.jobId!}`)}
                      >
                        Open the job
                      </Button>
                    )}
                  </div>
                )}
                {group.lines.map((line) => (
                  <div
                    key={line.id}
                    className="flex items-baseline gap-3 border-b border-line-soft px-3.5 py-2.5
                               last:border-b-0"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-cell text-text">{line.description}</span>
                      <span className="block text-sub text-text-muted">
                        {humanize(line.kind)}
                        {Number(line.qty) !== 1
                          && ` · ${Number(line.qty)} × ${formatDollars(Number(line.rate))}`}
                        {Number(line.taxAmount) > 0
                          && ` · tax ${formatDollars(Number(line.taxAmount))}`}
                      </span>
                    </span>
                    <span className="tnum shrink-0 text-cell text-text">
                      {formatDollars(Number(line.amount))}
                    </span>
                  </div>
                ))}
              </div>
            ))}

            <div className="flex items-baseline gap-3 border-t border-line bg-surface-alt
                            px-3.5 py-2.5">
              <span className="min-w-0 flex-1 text-cell font-semibold text-text">Total</span>
              <span className="tnum shrink-0 text-cell font-semibold text-text">
                {formatDollars(Number(invoice.total))}
              </span>
            </div>
          </>
        )}
    </Card>
  )
}

/**
 * What has come off it.
 *
 * Three kinds in one list and never summed together: a payment is money that
 * arrived, a credit says the customer never owed it, and a write-off says
 * they owe it and the company stopped asking. The tag is what says which,
 * because the three land on different QuickBooks accounts (§F.4) and read
 * very differently in a year-end review.
 */
function Money({ invoice }: { invoice: InvoiceRecordData }) {
  const WORDS: Record<Settlement['kind'], { label: string; tone: 'ok' | 'warn' | 'bad' }> = {
    payment: { label: 'Payment', tone: 'ok' },
    credit: { label: 'Credit', tone: 'warn' },
    write_off: { label: 'Written off', tone: 'bad' },
  }

  return (
    <Card className="overflow-hidden p-0">
      {invoice.settlements.length === 0
        ? (
          <p className="m-0 p-6 text-body text-text-muted">
            Nothing has come off it yet. A card or ACH payment lands here the moment the
            provider confirms it; a cheque lands when somebody records it.
          </p>
        )
        : invoice.settlements.map((settlement) => (
          <div
            key={`${settlement.kind}:${settlement.id}`}
            className="flex items-baseline gap-3 border-b border-line-soft px-3.5 py-2.5
                       last:border-b-0"
          >
            <Tag tone={WORDS[settlement.kind].tone}>{WORDS[settlement.kind].label}</Tag>
            <span className="min-w-0 flex-1">
              <span className="block text-cell text-text">
                {settlement.detail ? humanize(settlement.detail) : EM_DASH}
              </span>
              <span className="block text-sub text-text-muted">
                {formatRelative(settlement.at)}
              </span>
            </span>
            {settlement.status && settlement.status !== 'settled' && (
              <StatusChip
                group="invoice"
                status={settlement.status === 'pending' ? 'partially_paid' : 'bounced'}
              >
                {humanize(settlement.status)}
              </StatusChip>
            )}
            <span className="tnum shrink-0 text-cell text-text">
              {formatDollars(Number(settlement.amount))}
            </span>
          </div>
        ))}
    </Card>
  )
}
